import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import { createPortal } from "react-dom";
import BaseIcon from "../../svg_icons/BaseIcon";

const DROPDOWN_WIDTH = 240;
const DROPDOWN_GAP = 6;
const VIEWPORT_PADDING = 8;

const SidebarFloatingChildren = ({
    parentRef,
    isOpen,
    children = [],
    activeChild,
    onChildClick,
    onMouseEnter,
    onMouseLeave,
}) => {
    const dropdownRef = useRef(null);

    const [position, setPosition] = useState({
        top: 0,
        left: 0,
    });

    /*
    ============================================================
    CALCULATE DROPDOWN POSITION
    ============================================================
    
    IMPORTANT:

    The dropdown must always follow the RIGHT EDGE of the
    sidebar/group, not the cursor.

    parentRef points to the sidebar group, so we use its
    getBoundingClientRect() after the sidebar has started
    expanding.
    */
    const updatePosition = useCallback(() => {
        if (!isOpen || !parentRef?.current) {
            return;
        }

        const parentRect = parentRef.current.getBoundingClientRect();

        /*
        The dropdown should ALWAYS start from the right edge
        of the currently rendered sidebar.
        */
        let left = parentRect.right + DROPDOWN_GAP;
        let top = parentRect.top;

        /*
        ========================================================
        RIGHT EDGE PROTECTION
        ========================================================
        */

        if (
            left + DROPDOWN_WIDTH + VIEWPORT_PADDING >
            window.innerWidth
        ) {
            left =
                parentRect.left -
                DROPDOWN_WIDTH -
                DROPDOWN_GAP;
        }

        /*
        ========================================================
        VERTICAL POSITION
        ========================================================
        */

        const dropdownHeight =
            dropdownRef.current?.getBoundingClientRect()?.height || 0;

        if (
            dropdownHeight > 0 &&
            top + dropdownHeight + VIEWPORT_PADDING >
                window.innerHeight
        ) {
            top =
                window.innerHeight -
                dropdownHeight -
                VIEWPORT_PADDING;
        }

        if (top < VIEWPORT_PADDING) {
            top = VIEWPORT_PADDING;
        }

        if (left < VIEWPORT_PADDING) {
            left = VIEWPORT_PADDING;
        }

        setPosition({
            top,
            left,
        });
    }, [isOpen, parentRef]);

    /*
    ============================================================
    INITIAL POSITION
    ============================================================
    */

    useLayoutEffect(() => {
        if (!isOpen) {
            return;
        }

        /*
        Calculate immediately.
        */
        updatePosition();

        /*
        Then calculate again on the next animation frame.
        This is important because the sidebar width transition
        may not have completed yet.
        */
        const frame1 = requestAnimationFrame(() => {
            updatePosition();
        });

        const frame2 = requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                updatePosition();
            });
        });

        return () => {
            cancelAnimationFrame(frame1);
            cancelAnimationFrame(frame2);
        };
    }, [isOpen, children, updatePosition]);

    /*
    ============================================================
    TRACK SIDEBAR WIDTH TRANSITION
    ============================================================

    The sidebar has a CSS transition:

        width 0.28s ...

    Therefore the first measurement can happen before the
    transition finishes.

    ResizeObserver lets us recalculate whenever the parent
    element's rendered size changes.
    */

    useEffect(() => {
        if (!isOpen || !parentRef?.current) {
            return undefined;
        }

        const parentElement = parentRef.current;

        const resizeObserver = new ResizeObserver(() => {
            updatePosition();
        });

        resizeObserver.observe(parentElement);

        return () => {
            resizeObserver.disconnect();
        };
    }, [isOpen, parentRef, updatePosition]);

    /*
    ============================================================
    WINDOW RESIZE / SCROLL
    ============================================================
    */

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const handlePositionUpdate = () => {
            updatePosition();
        };

        window.addEventListener(
            "resize",
            handlePositionUpdate
        );

        window.addEventListener(
            "scroll",
            handlePositionUpdate,
            true
        );

        return () => {
            window.removeEventListener(
                "resize",
                handlePositionUpdate
            );

            window.removeEventListener(
                "scroll",
                handlePositionUpdate,
                true
            );
        };
    }, [isOpen, updatePosition]);

    /*
    ============================================================
    RENDER
    ============================================================
    */

    if (!isOpen || !children.length) {
        return null;
    }

    return createPortal(
        <div
            ref={dropdownRef}
            className="ca-sidebar-floating-children"
            style={{
                position: "fixed",
                top: `${position.top}px`,
                left: `${position.left}px`,
                width: `${DROPDOWN_WIDTH}px`,
            }}
            role="menu"
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            {children.map((child) => {
                const isActive = activeChild?.(child);

                return (
                    <div
                        key={child.title}
                        className={`ca-sidebar-floating-child ${
                            isActive ? "active" : ""
                        }`}
                        role="menuitem"
                        onClick={(event) => {
                            event.stopPropagation();
                            onChildClick?.(child, event);
                        }}
                    >
                        {child.icon && (
                            <BaseIcon
                                name={child.icon}
                                size="13"
                            />
                        )}

                        <span>{child.title}</span>
                    </div>
                );
            })}
        </div>,
        document.body
    );
};

export default SidebarFloatingChildren;
